'use client'

/**
 * MyCIO auth context — the React surface over the separate MyCIO Supabase client.
 * Every MyCIO page reads the session from here (never from the legacy useAuth hook).
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
  useRef,
} from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { getMyCIOClient, getMyCIOEmailLinkRequestClient } from '@/lib/mycio/supabase-mycio'
import {
  MYCIO_LANGUAGE_COOKIE,
  detectMyCIOLanguageFromNavigator,
  normalizeMyCIOLanguage,
} from '@/lib/mycio/language'

type AuthResult = { error: string | null }
// signUp surfaces the session too: if Supabase email-confirmation is OFF, signUp returns a live
// session and sends no code, so the caller must skip the (dead-end) OTP screen and go straight in.
type SignUpResult = { error: string | null; session: Session | null }
// emailRedirectTo: where the confirmation link lands (the sign-in flow passes /auth/callback with its mcp_return and
// next, so a sign-up started in the connector or at checkout continues there). data: extra user_metadata that must
// survive until the link is tapped, possibly in another browser (see src/lib/mycio/postSignup.ts).
type SignUpExtra = { emailRedirectTo?: string; data?: Record<string, string> }

interface MyCIOAuthValue {
  user: User | null
  session: Session | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<AuthResult>
  signUp: (email: string, password: string, name?: string, extra?: SignUpExtra) => Promise<SignUpResult>
  signInWithGoogle: () => Promise<AuthResult>
  signOut: () => Promise<void>
}

const MyCIOAuthContext = createContext<MyCIOAuthValue | null>(null)

function readSignupLanguage() {
  if (typeof document === 'undefined') return 'en'
  const raw = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${MYCIO_LANGUAGE_COOKIE}=`))
    ?.split('=')[1]
  return normalizeMyCIOLanguage(raw ? decodeURIComponent(raw) : null) ?? detectMyCIOLanguageFromNavigator()
}

function mycioAuthCallbackUrl(language: string = readSignupLanguage()) {
  if (typeof window === 'undefined') return undefined
  return `${window.location.origin}/auth/callback?mycio_lang=${encodeURIComponent(language)}`
}

// A MEMBER WHO DELETES THEIR ACCOUNT signs out on purpose and must land on the public home with the
// confirmation, not on /sign-in. The page that calls signOut arms this one-shot flag; the listener below
// consumes it and skips the expired-session redirect for that single sign-out.
let suppressNextSignOutRedirect = false
let suppressTimer: ReturnType<typeof setTimeout> | undefined
/** Arms the one-shot suppression. A backstop timer clears it after 5 s so it can never stay armed. */
export function suppressSignOutRedirectOnce(): void {
  suppressNextSignOutRedirect = true
  if (suppressTimer !== undefined) clearTimeout(suppressTimer)
  suppressTimer = setTimeout(() => {
    suppressNextSignOutRedirect = false
    suppressTimer = undefined
  }, 5000)
}
/** Clears the suppression as soon as the deliberate sign-out has finished (called in a finally by the caller). */
export function clearSuppressSignOutRedirect(): void {
  suppressNextSignOutRedirect = false
  if (suppressTimer !== undefined) clearTimeout(suppressTimer)
  suppressTimer = undefined
}

export function MyCIOAuthProvider({ children }: { children: ReactNode }) {
  const supabase = getMyCIOClient()
  const [session, setSession] = useState<Session | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  /** Did this browser hold a session at any point on this page? Distinguishes "signed out just now"
   *  from "arrived anonymous", which are the same `null` and must not be treated alike. */
  const hadSession = useRef(false)

  useEffect(() => {
    let mounted = true
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return
      setSession(data.session)
      setUser(data.session?.user ?? null)
      setLoading(false)
    })
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => {
      // AN EXPIRED SESSION GOES TO THE SIGN-IN PAGE, NOT TO A SIGNED-OUT SCREEN (founder,
      // 2026-09-12: "when the session is expired, redirect the user to the login page to re-login").
      //
      // Seen live: a stale token made `/assistant/opening` return the ANONYMOUS opener — "Sign in to
      // build my plan" — rendered inside a shell still showing the member's name and a Sign out
      // link. Every surface degraded differently because each decided for itself what a missing
      // token meant, and none of them said the true thing: you need to sign in again.
      //
      // ON THE TRANSITION ONLY. `hadSession` means this browser HELD a session during this page's
      // life and no longer does — expiry, a failed refresh, or signing out. Someone who arrived
      // anonymous never had one, so a public page is untouched. The sign-in page is excluded so a
      // sign-out cannot bounce against itself.
      if (hadSession.current && !s && typeof window !== 'undefined') {
        const path = window.location.pathname
        if (suppressNextSignOutRedirect) {
          suppressNextSignOutRedirect = false
        } else if (path.startsWith('/') && !path.startsWith('/sign-in')) {
          window.location.replace('/sign-in')
          return
        }
      }
      if (s) hadSession.current = true
      setSession(s)
      setUser(s?.user ?? null)
      setLoading(false)
    })

    // The SDK owns background refresh (autoRefreshToken:true on the single pinned client) and dedupes it, so
    // the context session stays fresh via onAuthStateChange('TOKEN_REFRESHED') without any manual timer/focus
    // ticks. We deliberately do NOT add extra refresh triggers here — every extra trigger is a chance to race
    // the SDK into a forced double-refresh, which was the storm.
    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [supabase])

  const signIn = useCallback(
    async (email: string, password: string): Promise<AuthResult> => {
      const { error } = await supabase.auth.signInWithPassword({ email, password })
      return { error: error?.message ?? null }
    },
    [supabase],
  )

  const signUp = useCallback(
    async (email: string, password: string, name?: string, extra?: SignUpExtra): Promise<SignUpResult> => {
      const language = readSignupLanguage()
      // THE CONFIRMATION LINK MUST WORK IN ANY BROWSER. A PKCE signUp mails a link that only completes in the browser
      // holding the code verifier, and people open it from a mail app. The non-PKCE request client gets a plain
      // token_hash, which the send-email hook links straight to /auth/callback (verifyOtp) — the same fix as reset.
      const { data, error } = await getMyCIOEmailLinkRequestClient().auth.signUp({
        email,
        password,
        options: {
          data: {
            ...(extra?.data ?? {}),
            ...(name ? { full_name: name } : {}),
            preferred_language: language,
            language,
            locale: language,
          },
          emailRedirectTo: extra?.emailRedirectTo ?? mycioAuthCallbackUrl(language),
        },
      })
      // A project that auto-confirms returns a session at once; that client does not persist, so hand it over.
      if (data?.session) {
        const { data: set } = await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        })
        return { error: error?.message ?? null, session: set?.session ?? data.session }
      }
      return { error: error?.message ?? null, session: null }
    },
    [supabase],
  )

  const signInWithGoogle = useCallback(async (): Promise<AuthResult> => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: mycioAuthCallbackUrl(),
        queryParams: { mycio_lang: readSignupLanguage() },
      },
    })
    return { error: error?.message ?? null }
  }, [supabase])

  const signOut = useCallback(async () => {
    await supabase.auth.signOut()
    setSession(null)
    setUser(null)
  }, [supabase])

  return (
    <MyCIOAuthContext.Provider
      value={{ user, session, loading, signIn, signUp, signInWithGoogle, signOut }}
    >
      {children}
    </MyCIOAuthContext.Provider>
  )
}

export function useMyCIOAuth(): MyCIOAuthValue {
  const ctx = useContext(MyCIOAuthContext)
  if (!ctx) throw new Error('useMyCIOAuth must be used within a MyCIOAuthProvider')
  return ctx
}

/**
 * Non-throwing variant for surfaces that render OUTSIDE the authenticated shell — e.g. the public
 * /plan/[token] share page, which mounts the language provider but no auth provider. Returns null there.
 */
export function useMyCIOAuthOptional(): MyCIOAuthValue | null {
  return useContext(MyCIOAuthContext)
}
